import { useQueryWithCacheExpiry } from "providers/Apollo/useQueryWithCacheExpiry";
import { PROFILE_DETAILS_QUERY } from "./queries";
import { useAuth } from "components/Auth";
import { useSessionFlag } from "hooks/useSessionFlag";
import { titleCase } from "./formatter";

export const CA_TIMEZONES = [
    'America/Atikokan',
    'America/Blanc-Sablon',
    'America/Cambridge_Bay',
    'America/Coral_Harbour',
    'America/Creston',
    'America/Dawson',
    'America/Dawson_Creek',
    'America/Edmonton',
    'America/Fort_Nelson',
    'America/Glace_Bay',
    'America/Goose_Bay',
    'America/Halifax',
    'America/Inuvik',
    'America/Iqaluit',
    'America/Moncton',
    'America/Montreal',
    'America/Nipigon',
    'America/Pangnirtung',
    'America/Rainy_River',
    'America/Rankin_Inlet',
    'America/Regina',
    'America/Resolute',
    'America/St_Johns',
    'America/Swift_Current',
    'America/Thunder_Bay',
    'America/Toronto',
    'America/Vancouver',
    'America/Whitehorse',
    'America/Winnipeg',
    'America/Yellowknife',
    'Canada/Atlantic',
    'Canada/Central',
    'Canada/Eastern',
    'Canada/Mountain',
    'Canada/Newfoundland',
    'Canada/Pacific',
    'Canada/Saskatchewan',
    'Canada/Yukon',
]

export const US_TIMEZONES = [
    'America/Adak',
    'America/Anchorage',
    'America/Atka',
    'America/Boise',
    'America/Chicago',
    'America/Denver',
    'America/Detroit',
    'America/Fort_Wayne',
    'America/Indiana/Indianapolis',
    'America/Indiana/Knox',
    'America/Indiana/Marengo',
    'America/Indiana/Petersburg',
    'America/Indiana/Tell_City',
    'America/Indiana/Vevay',
    'America/Indiana/Vincennes',
    'America/Indiana/Winamac',
    'America/Indianapolis',
    'America/Juneau',
    'America/Kentucky/Louisville',
    'America/Kentucky/Monticello',
    'America/Knox_IN',
    'America/Los_Angeles',
    'America/Louisville',
    'America/Menominee',
    'America/Metlakatla',
    'America/New_York',
    'America/Nome',
    'America/North_Dakota/Beulah',
    'America/North_Dakota/Center',
    'America/North_Dakota/New_Salem',
    'America/Phoenix',
    'America/Shiprock',
    'America/Sitka',
    'America/Yakutat',
    'Navajo',
    'Pacific/Honolulu',
    'US/Alaska',
    'US/Aleutian',
    'US/Arizona',
    'US/Central',
    'US/East-Indiana',
    'US/Eastern',
    'US/Hawaii',
    'US/Indiana-Starke',
    'US/Michigan',
    'US/Mountain',
    'US/Pacific'
]

export const AU_TIMEZONES = [
    'Antarctica/Macquarie',
    'Australia/ACT',
    'Australia/Adelaide',
    'Australia/Brisbane',
    'Australia/Broken_Hill',
    'Australia/Canberra',
    'Australia/Currie',
    'Australia/Darwin',
    'Australia/Eucla',
    'Australia/Hobart',
    'Australia/LHI',
    'Australia/Lindeman',
    'Australia/Lord_Howe',
    'Australia/Melbourne',
    'Australia/North',
    'Australia/NSW',
    'Australia/Perth',
    'Australia/Queensland',
    'Australia/South',
    'Australia/Sydney',
    'Australia/Tasmania',
    'Australia/Victoria',
    'Australia/West',
    'Australia/Yancowinna'
]

export const UK_TIMEZONES = [
    'Europe/Belfast',
    'Europe/London',
    'GB',
    'GB-Eire'
]

export enum COUNTRY_CODE {
    AUSTRALIA = "au",
    NZ = "nz",
    GB = "gb",
    UK = "uk",
    IRE = "ie",
    CANADA = "ca",
    USA = "us"
}

export enum OFFICE_TYPE {
    MAILING_ADDRESS = "MAIL",
    CLINICAL_OFFICE_EXTERNAL = "COUNEXE",
}

export const regionsToShowPreferredNumber = [
    COUNTRY_CODE.CANADA
]

export const REGION_HOSTNAMES = {
    'localhost': {
        regionCode: COUNTRY_CODE.CANADA,
        environment: 'local'
    },
    'caqaproviderhub.morneaushepell.com': {
        regionCode: COUNTRY_CODE.CANADA,
        environment: 'qa'
    },
    'cauat.theproviderhub.com': {
        regionCode: COUNTRY_CODE.CANADA,
        environment: 'uat'
    },
    'cauat.lespacefournisseurs.com': {
        regionCode: COUNTRY_CODE.CANADA,
        environment: 'uat'
    },
    'ca.theproviderhub.com': {
        regionCode: COUNTRY_CODE.CANADA,
        environment: 'prod'
    },
    'ca.lespacefournisseurs.com': {
        regionCode: COUNTRY_CODE.CANADA,
        environment: 'prod'
    },
    'usqaproviderhub.morneaushepell.com': {
        regionCode: COUNTRY_CODE.USA,
        environment: 'qa'
    },
    'usuat.theproviderhub.com': {
        regionCode: COUNTRY_CODE.USA,
        environment: 'uat'
    },
    'us.theproviderhub.com': {
        regionCode: COUNTRY_CODE.USA,
        environment: 'prod'
    },
    'ukqaproviderhub.morneaushepell.com': {
        regionCode: COUNTRY_CODE.UK,
        environment: 'qa'
    },
    'ukuat.theproviderhub.com': {
        regionCode: COUNTRY_CODE.UK,
        environment: 'uat'
    },
    'uk.theproviderhub.com': {
        regionCode: COUNTRY_CODE.UK,
        environment: 'prod'
    },
    'auqaproviderhub.morneaushepell.com': {
        regionCode: COUNTRY_CODE.AUSTRALIA,
        environment: 'qa'
    },
    'auuat.theproviderhub.com': {
        regionCode: COUNTRY_CODE.AUSTRALIA,
        environment: 'uat'
    },
    'au.theproviderhub.com': {
        regionCode: COUNTRY_CODE.AUSTRALIA,
        environment: 'prod'
    },
}

export const getAppRegionCode = (forcedRegion?: ValidAppRegion): COUNTRY_CODE => {
    if (typeof window !== 'undefined') {
        const { hostname } = window.location;
        if (hostname === 'localhost' && forcedRegion) {
            return forcedRegion?.split('-')[1].toLowerCase() as COUNTRY_CODE;
        }
        return REGION_HOSTNAMES[hostname]?.regionCode || '' as COUNTRY_CODE
    }
    return '' as COUNTRY_CODE
}

export const validRegions = ['fr-CA', 'en-CA', 'en-US', 'en-GB', 'en-AU', 'en-NZ', 'en-IE'] as const;

export type ValidAppRegion = typeof validRegions[number];

export const REGIONS_WITH_SUB_REGIONS: ValidAppRegion[] = ['en-GB', 'en-AU'] as const;

export function useProviderCountryCode(providerId?: number) {
    const forcedRegion = useSessionFlag<ValidAppRegion>('forced-region');
    const { effectiveProviderData } = useAuth();
    const resolvedProviderId = providerId || effectiveProviderData?.providerId;
    const skip = !resolvedProviderId || [0, '0'].includes(resolvedProviderId) || validRegions.includes(forcedRegion);
    const { data } = useQueryWithCacheExpiry(PROFILE_DETAILS_QUERY, {
        cacheLife: 1800000,
        fetchPolicy: 'cache-first',
        variables: {
            providerId: resolvedProviderId!
        },
        skip,
    });
    if (validRegions.includes(forcedRegion)) {
        return forcedRegion.split('-')[1].toLocaleLowerCase() as COUNTRY_CODE;
    }
    return (data?.provider?.contactInfo?.mailingAddresses?.[0]?.countryCode?.toLocaleLowerCase() || getAppRegionCode(forcedRegion)) as COUNTRY_CODE;
}

export function getProvinceOrStateCode(provinceOrStateName: string, country: keyof typeof RegionNameToRegionCodeMapping) {
    const targetCountry = countryNameToCountryCodeMapping[country?.includes(' ') ? titleCase(country) : country] || country;
    // console.info(`🌎 '${provinceOrStateName}' mapped to '${RegionNameToRegionCodeMapping[targetCountry]?.regions?.[titleCase(provinceOrStateName)]}'`, country);
    return RegionNameToRegionCodeMapping[targetCountry]?.regions?.[titleCase(provinceOrStateName.trim())] || provinceOrStateName;
}

export function getProvinceOrStateName(provinceOrStateCode: string, country: keyof typeof RegionCodeToRegionNameMapping) {
    return RegionCodeToRegionNameMapping[country]?.regions?.[provinceOrStateCode.trim().toUpperCase()] || provinceOrStateCode;
}

export const countryNameToCountryCodeMapping = {
    "United States of America": "US",
    "United States": "US",
    "USA": "US",
    Canada: "CA",
    Australia: "AU",
    "United Kingdom": "GB",
    "UK": "GB",
    Ireland: "IE",
    "New Zealand": "NZ"
};

export const RegionCodeToRegionNameMapping = {
  US: {
    country: "United States",
    regions: {
      AL: "Alabama", AK: "Alaska", AZ: "Arizona", AR: "Arkansas", CA: "California",
      CO: "Colorado", CT: "Connecticut", DE: "Delaware", FL: "Florida", GA: "Georgia",
      HI: "Hawaii", ID: "Idaho", IL: "Illinois", IN: "Indiana", IA: "Iowa",
      KS: "Kansas", KY: "Kentucky", LA: "Louisiana", ME: "Maine", MD: "Maryland",
      MA: "Massachusetts", MI: "Michigan", MN: "Minnesota", MS: "Mississippi", MO: "Missouri",
      MT: "Montana", NE: "Nebraska", NV: "Nevada", NH: "New Hampshire", NJ: "New Jersey",
      NM: "New Mexico", NY: "New York", NC: "North Carolina", ND: "North Dakota", OH: "Ohio",
      OK: "Oklahoma", OR: "Oregon", PA: "Pennsylvania", RI: "Rhode Island", SC: "South Carolina",
      SD: "South Dakota", TN: "Tennessee", TX: "Texas", UT: "Utah", VT: "Vermont",
      VA: "Virginia", WA: "Washington", WV: "West Virginia", WI: "Wisconsin", WY: "Wyoming",
      DC: "District of Columbia"
    }
  },
  CA: {
    country: "Canada",
    regions: {
      AB: "Alberta", BC: "British Columbia", MB: "Manitoba", NB: "New Brunswick",
      NL: "Newfoundland and Labrador", NS: "Nova Scotia", ON: "Ontario", PE: "Prince Edward Island",
      QC: "Quebec", SK: "Saskatchewan", NT: "Northwest Territories", NU: "Nunavut", YT: "Yukon"
    }
  },
  AU: {
    country: "Australia",
    regions: {
      NSW: "New South Wales", VIC: "Victoria", QLD: "Queensland", WA: "Western Australia",
      SA: "South Australia", TAS: "Tasmania", ACT: "Australian Capital Territory", NT: "Northern Territory"
    }
  },
  IE: {
    country: "Ireland",
    regions: {
      CW: "Carlow", CN: "Cavan", CE: "Clare", CO: "Cork", DL: "Donegal", D: "Dublin",
      G: "Galway", KY: "Kerry", KE: "Kildare", KK: "Kilkenny", LS: "Laois", LM: "Leitrim",
      LK: "Limerick", LD: "Longford", LH: "Louth", MO: "Mayo", MH: "Meath", MN: "Monaghan",
      OY: "Offaly", RN: "Roscommon", SO: "Sligo", TA: "Tipperary", WD: "Waterford",
      WH: "Westmeath", WX: "Wexford", WW: "Wicklow"
    }
  },
  NZ: {
    country: "New Zealand",
    regions: {
      AUK: "Auckland", BOP: "Bay of Plenty", CAN: "Canterbury", GIS: "Gisborne",
      HKB: "Hawke's Bay", MWT: "Manawatū-Whanganui", MBH: "Marlborough", NSN: "Nelson",
      NTL: "Northland", OTA: "Otago", STL: "Southland", TKI: "Taranaki", TAS: "Tasman",
      WKO: "Waikato", WGN: "Wellington", WTC: "West Coast"
    }
  },
  GB: {
    country: "United Kingdom",
    regions: {
      ENG: "England",
      SCT: "Scotland",
      WLS: "Wales",
      NIR: "Northern Ireland"
    }
  }
}

export const RegionNameToRegionCodeMapping = {
  US: {
    country: "United States",
    regions: {
        Alabama: "AL", Alaska: "AK", Arizona: "AZ", Arkansas: "AR", California: "CA",
        Colorado: "CO", Connecticut: "CT", Delaware: "DE", "District Of Columbia": "DC", Florida: "FL",
        Georgia: "GA", Hawaii: "HI", Idaho: "ID", Illinois: "IL", Indiana: "IN", Iowa: "IA",
        Kansas: "KS", Kentucky: "KY", Louisiana: "LA", Maine: "ME", Maryland: "MD", Massachusetts: "MA",
        Michigan: "MI", Minnesota: "MN", Mississippi: "MS", Missouri: "MO", Montana: "MT", Nebraska: "NE",
        Nevada: "NV", "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
        "North Carolina": "NC", "North Dakota": "ND", Ohio: "OH", Oklahoma: "OK", Oregon: "OR",
        Pennsylvania: "PA", "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD", Tennessee: "TN",
        Texas: "TX", Utah: "UT", Vermont: "VT", Virginia: "VA", Washington: "WA", "West Virginia": "WV",
        Wisconsin: "WI", Wyoming: "WY"
    }
  },
  CA: {
    country: "Canada",
    regions: {
        Alberta: "AB", "British Columbia": "BC", Manitoba: "MB", "New Brunswick": "NB",
        "Newfoundland And Labrador": "NL", "Northwest Territories": "NT", "Nova Scotia": "NS", Nunavut: "NU",
        Ontario: "ON", "Prince Edward Island": "PE", Quebec: "QC", Saskatchewan: "SK", Yukon: "YT"
    }
  },
  AU: {
    country: "Australia",
    regions: {
        "Australian Capital Territory": "ACT", "New South Wales": "NSW", "Northern Territory": "NT",
        Queensland: "QLD", "South Australia": "SA", Tasmania: "TAS", Victoria: "VIC", "Western Australia": "WA"
    }
  },
  IE: {
    country: "Ireland",
    regions: {
        Carlow: "CW", Cavan: "CN", Clare: "CE", Cork: "CO", Donegal: "DL", Dublin: "D", Galway: "G",
        Kerry: "KY", Kildare: "KE", Kilkenny: "KK", Laois: "LS", Leitrim: "LM", Limerick: "LK",
        Longford: "LD", Louth: "LH", Mayo: "MO", Meath: "MH", Monaghan: "MN", Offaly: "OY",
        Roscommon: "RN", Sligo: "SO", Tipperary: "TA", Waterford: "WD", Westmeath: "WH", Wexford: "WX", Wicklow: "WW"
    }
  },
  NZ: {
    country: "New Zealand",
    regions: {
        Auckland: "AUK", "Bay of Plenty": "BOP", Canterbury: "CAN", Gisborne: "GIS", "Hawke's Bay": "HKB",
        "Manawatū-Whanganui": "MWT", Marlborough: "MBH", Nelson: "NSN", Northland: "NTL", Otago: "OTA",
        Southland: "STL", Taranaki: "TKI", Tasman: "TAS", Waikato: "WKO", Wellington: "WGN", "West Coast": "WTC"
    }
  },
  GB: {
    country: "United Kingdom",
    regions: {
        England: "ENG", Scotland: "SCT", Wales: "WLS", "Northern Ireland": "NIR"
    }
  }
};
